import { useState, useCallback, useRef, type ReactNode } from 'react'
import { Portal } from '../portal'
import { ShieldAlert } from 'lucide-react'
import { isNsfwPrompt, isAgeVerified, setAgeVerified } from '@/lib/nsfw-check'

const AGE_VERIFICATION_MODAL_ID = 'age-verification-modal'

interface AgeVerificationProps {
  children: ReactNode
}

/**
 * Legacy wrapper component — wraps children and blocks rendering until verified.
 * Kept for backwards compatibility.
 */
export function AgeVerification({ children }: AgeVerificationProps) {
  const { isVerified, showModal, handleConfirm, handleDecline } = useAgeVerification()

  if (showModal) {
    return <AgeVerificationModal onConfirm={handleConfirm} onDecline={handleDecline} />
  }

  if (!isVerified) return null

  return <>{children}</>
}

/**
 * Hook for programmatic age verification.
 * Call `requireVerification(prompt)` before submitting — it returns true if
 * the prompt is clean or user is already verified, or shows the modal and
 * resolves once the user confirms.
 */
export function useAgeVerification() {
  const [isVerified, setIsVerifiedState] = useState(isAgeVerified)
  const [showModal, setShowModal] = useState(false)
  const pendingResolveRef = useRef<((v: boolean) => void) | null>(null)

  const handleConfirm = useCallback(() => {
    setAgeVerified()
    setIsVerifiedState(true)
    setShowModal(false)
    pendingResolveRef.current?.(true)
    pendingResolveRef.current = null
  }, [])

  const handleDecline = useCallback(() => {
    setShowModal(false)
    pendingResolveRef.current?.(false)
    pendingResolveRef.current = null
  }, [])

  const requireVerification = useCallback((prompt: string | undefined): Promise<boolean> => {
    if (!prompt || !isNsfwPrompt(prompt)) return Promise.resolve(true)
    if (isAgeVerified()) return Promise.resolve(true)

    return new Promise<boolean>((resolve) => {
      pendingResolveRef.current = resolve
      setShowModal(true)
    })
  }, [])

  return { isVerified, showModal, handleConfirm, handleDecline, requireVerification }
}

export function AgeVerificationModal({
  onConfirm,
  onDecline,
}: {
  onConfirm: () => void
  onDecline: () => void
}) {
  return (
    <Portal>
      <div
        id={AGE_VERIFICATION_MODAL_ID}
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
      >
        <div className="max-w-md w-full">
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-orange-500/10 border border-orange-500/20">
              <ShieldAlert className="size-10 text-orange-500" />
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-2">
            Age Verification Required
          </h2>

          <p className="text-[#888] text-center mb-8">
            Your prompt contains mature content intended for adults only.
            By continuing, you confirm that you are at least 18 years old.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={onConfirm}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 text-white font-medium hover:from-violet-500 hover:to-violet-400 transition-all"
            >
              I am 18 or older — Continue
            </button>
            <button
              onClick={onDecline}
              className="w-full py-3 px-4 rounded-xl bg-white/[0.05] border border-white/[0.1] text-[#888] font-medium hover:bg-white/[0.08] hover:text-white transition-all"
            >
              Go back
            </button>
          </div>

          <p className="text-xs text-[#555] text-center mt-6">
            By clicking "I am 18 or older", you agree that you are of legal age
            to view adult content in your jurisdiction.
          </p>
        </div>
      </div>
    </Portal>
  )
}
